import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  Box,
  Typography,
  Divider,
  Button,
  Chip,
  Avatar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tooltip,
  Backdrop,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';

const GENDER_OPTIONS = [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }];
const MARITAL_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];
const RELIGION_OPTIONS = ['Hinduism', 'Islam', 'Christianity', 'Sikhism', 'Buddhism', 'Jainism', 'Others'];

const DOC_TYPES = [
  { label: 'Aadhaar', key: 'aadhaar', showIssuedBy: false, showValidTill: false },
  { label: 'PAN', key: 'pan', showIssuedBy: false, showValidTill: false },
  { label: 'Voter Card', key: 'voter', showIssuedBy: false, showValidTill: false },
  { label: 'Passport', key: 'passport', showIssuedBy: true, showValidTill: true },
  { label: 'Driving License', key: 'driving-liciense', showIssuedBy: true, showValidTill: true },
];

const InfoRow = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body1" fontWeight="medium">{value || '—'}</Typography>
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

InfoRow.defaultProps = {
  value: '',
};

function DonorDetails() {
  const { external_code } = useParams();
  const navigate = useNavigate();

  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Document upload state
  const [docFiles, setDocFiles] = useState({});
  const [docPreviews, setDocPreviews] = useState({});
  const [docUploading, setDocUploading] = useState({});
  const [docUploaded, setDocUploaded] = useState({});
  const [selectedDocTab, setSelectedDocTab] = useState(0);

  const loadDonor = async () => {
    if (!external_code) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.contacts.getDonorDetails(external_code);
      if (res?.data) {
        setDonor(res.data.donor || res.data);
      } else {
        setError('Donor not found');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load donor details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDonor(); }, [external_code]);

  const startEdit = () => {
    setEditForm({
      first_name: donor?.first_name || '',
      middle_name: donor?.middle_name || '',
      last_name: donor?.last_name || '',
      gender: donor?.gender || '',
      marital_status: donor?.marital_status || '',
      date_of_birth: donor?.date_of_birth ? donor.date_of_birth.substring(0, 10) : '',
      age: donor?.age || '',
      email_address: donor?.email_address || '',
      religion: donor?.religion || '',
      nationality: donor?.nationality || '',
      occupation: donor?.occupation || '',
      identification_mark: donor?.identification_mark || '',
      remarks: donor?.remarks || '',
    });
    setEditing(true);
  };

  const handleEditChange = (field, value) => {
    setEditForm((p) => ({ ...p, [field]: typeof value === 'string' ? value.toUpperCase() : value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.contacts.editDonor(donor.id, editForm);
      await loadDonor();
      setEditing(false);
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Donor updated successfully.', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const handleDocNumberChange = (docKey, value) => {
    setDocFiles((p) => ({ ...p, [docKey]: { ...(p[docKey] || {}), number: value.toUpperCase() } }));
  };

  const handleDocFileChange = (docKey, side, file) => {
    setDocFiles((p) => ({ ...p, [docKey]: { ...(p[docKey] || {}), [side]: file } }));
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setDocPreviews((p) => ({ ...p, [`${docKey}_${side}`]: url }));
    } else {
      setDocPreviews((p) => ({ ...p, [`${docKey}_${side}`]: null }));
    }
  };

  const handleDocUpload = async (doc) => {
    const d = docFiles[doc.key] || {};
    if (!d.number) { Swal.fire({ icon: 'warning', title: 'Missing document number' }); return; }
    if (!d.front) { Swal.fire({ icon: 'warning', title: 'Missing front image' }); return; }
    setDocUploading((p) => ({ ...p, [doc.key]: true }));
    try {
      const fd = new FormData();
      fd.append('contact_id', donor.id);
      fd.append('document_number', d.number);
      fd.append('type', doc.key === 'voter' ? 'epic' : doc.key);
      fd.append('file1', d.front);
      if (d.back) fd.append('file2', d.back);
      fd.append('is_default', 'Y');
      fd.append('file_sub_type_1', `front_${doc.key}`);
      if (d.back) fd.append('file_sub_type_2', `back_${doc.key}`);
      if (doc.showIssuedBy && d.issued_by) fd.append('issued_by', d.issued_by);
      if (doc.showValidTill && d.valid_till) fd.append('valid_till', d.valid_till);
      await api.contacts.uploadDonorDocuments(fd);
      setDocUploaded((p) => ({ ...p, [doc.key]: true }));
      await loadDonor();
      Swal.fire({ icon: 'success', title: `${doc.label} uploaded!`, timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Upload failed', text: err?.response?.data?.message || 'Failed to upload document' });
    } finally {
      setDocUploading((p) => ({ ...p, [doc.key]: false }));
    }
  };

  const getInitials = () =>
    `${(donor?.first_name || '')[0] || ''}${(donor?.last_name || '')[0] || ''}`.toUpperCase();

  const fullName = donor
    ? [donor.first_name, donor.middle_name, donor.last_name].filter(Boolean).join(' ')
    : '';

  if (loading && !donor) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <LoaderOverlay open={loading} />
        <Footer />
      </DashboardLayout>
    );
  }

  if (error && !donor) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{error}</Typography>
            <Button onClick={() => navigate('/donor-list')} sx={{ mt: 2 }} startIcon={<ArrowBackIcon />}>Back to List</Button>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading || saving} />

      <MDBox pt={4} pb={4} px={2}>
        {/* Back + title */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/donor-list')}>
            <ArrowBackIcon />
          </IconButton>
          <MDTypography variant="h4" fontWeight="bold">Donor Details</MDTypography>
        </Box>

        {donor && (
          <>
            {/* Profile header card */}
            <Card sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={3}>
                <Avatar sx={{ bgcolor: donor.gender === 'M' ? '#1976d2' : '#e91e8c', width: 64, height: 64, fontSize: 22 }}>
                  {getInitials()}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight="bold">{fullName}</Typography>
                  <Typography variant="body2" color="text.secondary">{donor.external_code}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={donor.gender === 'M' ? 'Male' : 'Female'} size="small" color={donor.gender === 'M' ? 'info' : 'secondary'} />
                    {donor.marital_status && <Chip label={donor.marital_status} size="small" variant="outlined" />}
                    {donor.date_of_birth && (
                      <Chip label={`DOB: ${new Date(donor.date_of_birth).toLocaleDateString()}`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
                {!editing ? (
                  <button
                    type="button"
                    onClick={startEdit}
                    style={{
                      padding: '8px 20px',
                      background: '#00798E',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'background 0.3s ease',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
                    onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    Edit
                  </button>
                ) : (
                  <Box display="flex" gap={1}>
                    <button
                      type="button"
                      onClick={handleSave}
                      style={{
                        padding: '8px 20px',
                        background: '#00798E',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'background 0.3s ease',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
                      onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
                    >
                      <SaveIcon style={{ fontSize: 16 }} />
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      style={{
                        padding: '8px 20px',
                        background: '#e0e0e0',
                        color: '#333',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background 0.3s ease',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#bdbdbd')}
                      onMouseOut={(e) => (e.currentTarget.style.background = '#e0e0e0')}
                    >
                      Cancel
                    </button>
                  </Box>
                )}
              </Box>
            </Card>

            {/* Tabs */}
            <Card sx={{ mb: 3 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <Tab label="Personal Info" />
                <Tab label="Contact" />
                <Tab label="Documents" />
              </Tabs>

              {/* Tab 0 – Personal */}
              {tab === 0 && (
                <Box p={3}>
                  {!editing ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}><InfoRow label="First Name" value={donor.first_name} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Middle Name" value={donor.middle_name} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Last Name" value={donor.last_name} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Gender" value={donor.gender === 'M' ? 'Male' : 'Female'} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Marital Status" value={donor.marital_status} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Date of Birth" value={donor.date_of_birth ? new Date(donor.date_of_birth).toLocaleDateString() : ''} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Age" value={donor.age} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Religion" value={donor.religion} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Nationality" value={donor.nationality} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Occupation" value={donor.occupation} /></Grid>
                      <Grid item xs={12} sm={4}><InfoRow label="Identification Mark" value={donor.identification_mark} /></Grid>
                      <Grid item xs={12} sm={8}><InfoRow label="Remarks" value={donor.remarks} /></Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {[
                        { field: 'first_name', label: 'First Name', type: 'text' },
                        { field: 'middle_name', label: 'Middle Name', type: 'text' },
                        { field: 'last_name', label: 'Last Name', type: 'text' },
                        { field: 'date_of_birth', label: 'Date of Birth', type: 'date' },
                        { field: 'age', label: 'Age', type: 'number' },
                        { field: 'email_address', label: 'Email', type: 'text' },
                        { field: 'nationality', label: 'Nationality', type: 'text' },
                        { field: 'occupation', label: 'Occupation', type: 'text' },
                        { field: 'identification_mark', label: 'Identification Mark', type: 'text' },
                      ].map(({ field, label, type }) => (
                        <Grid item xs={12} sm={4} key={field}>
                          <TextField
                            fullWidth label={label} type={type} value={editForm[field] || ''}
                            onChange={(e) => handleEditChange(field, type === 'text' ? e.target.value.toUpperCase() : e.target.value)}
                            InputLabelProps={type === 'date' ? { shrink: true } : {}}
                            inputProps={type === 'text' ? { style: { textTransform: 'uppercase' } } : {}}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Gender</InputLabel>
                          <Select value={editForm.gender || ''} label="Gender" onChange={(e) => handleEditChange('gender', e.target.value)} sx={{ height: 45 }}>
                            {GENDER_OPTIONS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Marital Status</InputLabel>
                          <Select value={editForm.marital_status || ''} label="Marital Status" onChange={(e) => handleEditChange('marital_status', e.target.value)} sx={{ height: 45 }}>
                            {MARITAL_STATUS_OPTIONS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Religion</InputLabel>
                          <Select value={editForm.religion || ''} label="Religion" onChange={(e) => handleEditChange('religion', e.target.value)} sx={{ height: 45 }}>
                            {RELIGION_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="Remarks" value={editForm.remarks || ''}
                          onChange={(e) => handleEditChange('remarks', e.target.value.toUpperCase())}
                          inputProps={{ style: { textTransform: 'uppercase' } }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}

              {/* Tab 1 – Contact */}
              {tab === 1 && (
                <Box p={3}>
                  {donor.phones?.length > 0 && (
                    <>
                      <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>Phone Numbers</MDTypography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2} mb={3}>
                        {donor.phones.map((ph) => (
                          <Grid item xs={12} sm={4} key={ph.id}>
                            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight="medium">+{ph.country_code} {ph.phone_number}</Typography>
                                {ph.is_primary === 'Y' && <Chip label="Primary" size="small" color="success" />}
                                {ph.is_whatsapp === 'Y' && <Chip label="WhatsApp" size="small" color="info" />}
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}

                  {donor.addresses?.length > 0 && (
                    <>
                      <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>Addresses</MDTypography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        {donor.addresses.map((addr) => (
                          <Grid item xs={12} sm={6} key={addr.id}>
                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                              <Typography variant="body2">{addr.address_line_1}</Typography>
                              {addr.address_line_2 && <Typography variant="body2">{addr.address_line_2}</Typography>}
                              <Typography variant="body2" color="text.secondary">
                                {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Box>
              )}

              {/* Tab 2 – Documents */}
              {tab === 2 && (
                <Box p={3}>
                  {donor.documents?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Document Number</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Issued By</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Valid Till</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Files</TableCell>
                          </TableRow>
                          {donor.documents.map((doc) => (
                            <TableRow key={doc.id} hover>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={0.75}>
                                  <DescriptionIcon sx={{ color: '#00798E', fontSize: 17 }} />
                                  <Typography variant="body2" fontWeight="medium">
                                    {doc.documentType?.type_name || '—'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold" letterSpacing={0.5}>
                                  {doc.document_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {doc.issued_by || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {doc.valid_till ? new Date(doc.valid_till).toLocaleDateString('en-GB') : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {doc.is_verified === 'Y' ? (
                                  <Box display="flex" alignItems="center" gap={0.5} color="green">
                                    <span>✓</span>
                                    <Typography variant="caption" fontWeight="medium">Verified</Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="warning.main" fontWeight="medium">
                                    Pending
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  {doc.files?.length > 0 ? (
                                    doc.files.map((file) => {
                                      const fileUrl = `${file.base_url}${file.file_path}`;
                                      const rawLabel = file.sub_type
                                        ? file.sub_type.replace(/_/g, ' ').toUpperCase()
                                        : 'File';
                                      const label = file.sub_type
                                        ? file.sub_type.split('_')[0].charAt(0).toUpperCase() + file.sub_type.split('_')[0].slice(1)
                                        : 'View';
                                      return (
                                        <Tooltip key={file.id} title={rawLabel} arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() => window.open(fileUrl, '_blank')}
                                            sx={{
                                              color: '#00798E',
                                              border: '1px solid #00798E',
                                              borderRadius: '6px',
                                              padding: '4px 8px',
                                              fontSize: 11,
                                              gap: 0.5,
                                              '&:hover': { backgroundColor: 'rgba(0,121,142,0.08)', transform: 'scale(1.05)' },
                                              transition: 'all 0.2s',
                                            }}
                                          >
                                            <VisibilityIcon sx={{ fontSize: 14 }} />
                                            <span style={{ fontWeight: 600 }}>{label}</span>
                                          </IconButton>
                                        </Tooltip>
                                      );
                                    })
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">No files</Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box textAlign="center" py={4}>
                      <DescriptionIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                      <Typography color="text.secondary">No documents uploaded</Typography>
                    </Box>
                  )}

                  {/* Upload New Document */}
                  <Box mt={4}>
                    <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>Upload New Document</MDTypography>
                    <Divider sx={{ mb: 2 }} />

                    <Tabs
                      value={selectedDocTab}
                      onChange={(_, v) => setSelectedDocTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ mb: 3, borderBottom: '1px solid #e0e0e0' }}
                    >
                      {DOC_TYPES.map((doc) => (
                        <Tab
                          key={doc.key}
                          label={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {docUploaded[doc.key]
                                ? <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 17 }} />
                                : <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd', fontSize: 17 }} />}
                              <span>{doc.label}</span>
                            </Box>
                          }
                        />
                      ))}
                    </Tabs>

                    {DOC_TYPES.map((doc, tabIdx) => {
                      if (selectedDocTab !== tabIdx) return null;
                      const d = docFiles[doc.key] || {};
                      const isUploading = docUploading[doc.key];
                      const isUploaded = docUploaded[doc.key];
                      const frontPreview = docPreviews[`${doc.key}_front`];
                      const backPreview = docPreviews[`${doc.key}_back`];
                      return (
                        <Box key={doc.key}>
                          {isUploaded && (
                            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                              <strong>{doc.label}</strong> uploaded successfully!
                            </Alert>
                          )}
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={doc.showIssuedBy || doc.showValidTill ? 4 : 6}>
                              <TextField
                                fullWidth
                                label={`${doc.label} Number *`}
                                value={d.number || ''}
                                onChange={(e) => handleDocNumberChange(doc.key, e.target.value)}
                                inputProps={{ style: { textTransform: 'uppercase' } }}
                                disabled={isUploaded}
                              />
                            </Grid>
                            {doc.showIssuedBy && (
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  fullWidth
                                  label="Issued By"
                                  value={d.issued_by || ''}
                                  onChange={(e) =>
                                    setDocFiles((p) => ({ ...p, [doc.key]: { ...(p[doc.key] || {}), issued_by: e.target.value.toUpperCase() } }))
                                  }
                                  inputProps={{ style: { textTransform: 'uppercase' } }}
                                  disabled={isUploaded}
                                />
                              </Grid>
                            )}
                            {doc.showValidTill && (
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  fullWidth
                                  type="date"
                                  label="Valid Till"
                                  InputLabelProps={{ shrink: true }}
                                  value={d.valid_till || ''}
                                  onChange={(e) =>
                                    setDocFiles((p) => ({ ...p, [doc.key]: { ...(p[doc.key] || {}), valid_till: e.target.value } }))
                                  }
                                  disabled={isUploaded}
                                />
                              </Grid>
                            )}

                            {/* Front Side */}
                            <Grid item xs={12} sm={6}>
                              <input
                                id={`doc-front-${doc.key}`}
                                type="file"
                                accept="image/*,application/pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => handleDocFileChange(doc.key, 'front', e.target.files[0])}
                              />
                              <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" mb={0.5}>
                                Front Side <span style={{ color: 'red' }}>*</span>
                              </Typography>
                              <Box
                                onClick={() => !isUploaded && document.getElementById(`doc-front-${doc.key}`).click()}
                                sx={{
                                  border: '2px dashed',
                                  borderColor: d.front ? '#00798E' : '#e0e0e0',
                                  borderRadius: 2,
                                  p: 2,
                                  minHeight: 150,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: isUploaded ? 'default' : 'pointer',
                                  backgroundColor: d.front ? 'rgba(0,121,142,0.04)' : '#fafafa',
                                  transition: 'all 0.2s',
                                  overflow: 'hidden',
                                  '&:hover': !isUploaded ? { borderColor: '#00798E', backgroundColor: 'rgba(0,121,142,0.06)' } : {},
                                }}
                              >
                                {frontPreview ? (
                                  <Box textAlign="center">
                                    <img src={frontPreview} alt="front" style={{ maxHeight: 110, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }} />
                                    <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ mt: 0.5 }}>{d.front?.name}</Typography>
                                  </Box>
                                ) : d.front ? (
                                  <Box textAlign="center">
                                    <DescriptionIcon sx={{ fontSize: 40, color: '#00798E' }} />
                                    <Typography variant="caption" display="block" noWrap>{d.front.name}</Typography>
                                  </Box>
                                ) : (
                                  <Box textAlign="center">
                                    <CloudUploadIcon sx={{ fontSize: 38, color: '#bdbdbd' }} />
                                    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight="medium">Click to upload front</Typography>
                                    <Typography variant="caption" color="text.disabled">Image or PDF</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* Back Side */}
                            <Grid item xs={12} sm={6}>
                              <input
                                id={`doc-back-${doc.key}`}
                                type="file"
                                accept="image/*,application/pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => handleDocFileChange(doc.key, 'back', e.target.files[0])}
                              />
                              <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" mb={0.5}>
                                Back Side <span style={{ color: '#888', fontSize: 11 }}>(optional)</span>
                              </Typography>
                              <Box
                                onClick={() => !isUploaded && document.getElementById(`doc-back-${doc.key}`).click()}
                                sx={{
                                  border: '2px dashed',
                                  borderColor: d.back ? '#00798E' : '#e0e0e0',
                                  borderRadius: 2,
                                  p: 2,
                                  minHeight: 150,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: isUploaded ? 'default' : 'pointer',
                                  backgroundColor: d.back ? 'rgba(0,121,142,0.04)' : '#fafafa',
                                  transition: 'all 0.2s',
                                  overflow: 'hidden',
                                  '&:hover': !isUploaded ? { borderColor: '#00798E', backgroundColor: 'rgba(0,121,142,0.06)' } : {},
                                }}
                              >
                                {backPreview ? (
                                  <Box textAlign="center">
                                    <img src={backPreview} alt="back" style={{ maxHeight: 110, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }} />
                                    <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ mt: 0.5 }}>{d.back?.name}</Typography>
                                  </Box>
                                ) : d.back ? (
                                  <Box textAlign="center">
                                    <DescriptionIcon sx={{ fontSize: 40, color: '#00798E' }} />
                                    <Typography variant="caption" display="block" noWrap>{d.back.name}</Typography>
                                  </Box>
                                ) : (
                                  <Box textAlign="center">
                                    <CloudUploadIcon sx={{ fontSize: 38, color: '#bdbdbd' }} />
                                    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight="medium">Click to upload back</Typography>
                                    <Typography variant="caption" color="text.disabled">Image or PDF</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* Upload Button */}
                            <Grid item xs={12}>
                              <Box display="flex" justifyContent="flex-end">
                                <button
                                  type="button"
                                  onClick={() => handleDocUpload(doc)}
                                  disabled={isUploaded || isUploading || !d.number || !d.front}
                                  style={{
                                    padding: '10px 24px',
                                    background: (isUploaded || isUploading || !d.number || !d.front) ? '#cccccc' : '#00798E',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: (isUploaded || isUploading || !d.number || !d.front) ? 'not-allowed' : 'pointer',
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    minWidth: 160,
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'background 0.3s ease',
                                  }}
                                  onMouseOver={(e) => { if (!isUploaded && !isUploading && d.number && d.front) e.currentTarget.style.background = '#006379'; }}
                                  onMouseOut={(e) => { if (!isUploaded && !isUploading && d.number && d.front) e.currentTarget.style.background = '#00798E'; }}
                                >
                                  {isUploading
                                    ? <><CircularProgress size={16} style={{ color: '#fff' }} /> Uploading...</>
                                    : isUploaded
                                      ? `Uploaded ✓`
                                      : <><CloudUploadIcon style={{ fontSize: 18 }} /> {`Upload ${doc.label}`}</>
                                  }
                                </button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Card>
          </>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default DonorDetails;
